/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useRef, useState } from "react";

import { loginUserSessionService } from "../../services/userService"


// reactstrap components
// Only the grid is still Argon's. The card, fields and buttons are now the
// brand-auth primitives — see styles/brand-auth.css.
import { Container, Row, Col } from "reactstrap";
import { useNavigate } from "react-router-dom";
// core components
import "../../styles/brand-auth.css";
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import { useUserAuth } from "../../context/UserContext";
import { SHOW_GOOGLE_SIGN_IN } from "../../config/googleAuth";
import { useLesson } from "../../hooks/lessonFramework";
import useReveal from "hooks/useReveal";
import { useTranslation, Trans } from "react-i18next";
import logoVideo from "../../assets/videos/loadingCircleBlackOnWhite1080.mp4";

// The looping logo is decoration. Visitors who ask for less motion get the
// loading text without it.
const prefersReducedMotion = () =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

// Firebase sign-in error codes, mapped to translation keys. Shared keys are
// reused where the wording is the same.
const LOGIN_ERROR_KEYS = {
  "auth/invalid-credential": "desktopAuth.errorCredentials",
  "auth/wrong-password": "desktopAuth.errorCredentials",
  "auth/user-not-found": "desktopAuth.errorCredentials",
  "auth/invalid-email": "register.errors.invalidEmail",
  "auth/too-many-requests": "register.errors.tooManyRequests",
  "auth/user-disabled": "login.errors.userDisabled",
  "auth/network-request-failed": "register.errors.network",
};

const Login = () => {

  // The card's entrance — see styles/premium.css.
  useReveal();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const mainRef = useRef(null);
  const navigate = useNavigate();
  const { setUser, user, logIn, googleSignIn } = useUserAuth();
  const { t, i18n } = useTranslation();

  // OLD LOGIN
  // const { logIn } = useUserAuth();

  const [isLogingIn, setIsLogingIn] = useState(false);

  // Framework call example
  const { variable1, variable2, method1, method2 } = useLesson();



  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      setIsLogingIn(true);

      const loggedUser = await logIn(email, password);

      await loginUserSessionService(loggedUser.accessToken)
      navigate("/home");
    } catch (error) {
      setError(getAuthErrorMessage(error));
      // setError(error.message);
    } finally {
      setIsLogingIn(false);
    }
  };


  const submitSingInWithGoogle = async (event) => {
    try {
      // alert("Google button clicked for SignIn");
      await googleSignIn();
      navigate("/home");
    } catch (error) {
      // alert("ALERT ERROR FOR GOOGLE");
      setError(getAuthErrorMessage(error));
    }
  };



  const getAuthErrorMessage = (error) => {
    const code = error?.code || error?.errorCode;
    return t(LOGIN_ERROR_KEYS[code] || "desktopAuth.errorSignInFailed");
  };





  // OLD LOGIN. 
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setError("");
  //   try {
  //     const responseUser = await loginUser(email, password);
  //     setUser(responseUser)
  //     navigate("/home");
  //   } catch (error) {
  //     setError(error.message);
  //   }
  // };

  return (
    <>
      <DemoNavbar />
      <main className="cn-auth" ref={mainRef}>

        {isLogingIn && (
          <div className="courses-loading-overlay" role="alert" aria-busy="true" aria-live="polite">
            <div className="courses-loading-card">
              {!prefersReducedMotion() && (
                <video
                  src={logoVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  draggable={false}
                  aria-hidden="true"
                  style={{ width: 72, height: 72, display: "block" }}
                />
              )}
              <div className="courses-loading-text">{t("desktopAuth.signingIn")}</div>
            </div>
          </div>
        )}

        <section className="cn-auth-hero cn-grid">
          <span className="cn-grain" aria-hidden="true" />
          <span className="cn-orb cn-orb-1" />
          <span className="cn-orb cn-orb-2" />
          <span className="cn-orb cn-orb-3" />
          <Container>
            <Row className="justify-content-center">
              <Col xs="12" md="10" lg="6" xl="5">
                <div className="cn-card" data-reveal>
                  <div className="cn-card-head">
                    <h1 className="cn-title">{t("login.login")}</h1>
                  </div>

                  {SHOW_GOOGLE_SIGN_IN && (
                    <>
                      <button
                        className="cn-social"
                        type="button"
                        onClick={submitSingInWithGoogle}
                      >
                        <img
                          alt=""
                          aria-hidden="true"
                          src={
                            require("assets/img/icons/common/google.svg")
                              .default
                          }
                        />
                        <span>{t("login.signinWith")} Google</span>
                      </button>
                      <div className="cn-divider">{t("login.orSigninWith")}</div>
                    </>
                  )}

                  <form onSubmit={handleSubmit} role="form">
                    <div className="cn-field">
                      <label className="cn-label" htmlFor="login-email">
                        {t("login.email")}
                      </label>
                      {/* The icon is decorative now that the field has a real
                          label, so it is aria-hidden rather than announced. */}
                      <div className="cn-input-wrap">
                        <i className="ni ni-email-83 cn-input-icon" aria-hidden="true" />
                        <input
                          id="login-email"
                          className="cn-input"
                          type="email"
                          autoComplete="email"
                          placeholder={t("login.email")}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="cn-field cn-field-last">
                      <label className="cn-label" htmlFor="login-password">
                        {t("login.password")}
                      </label>
                      <div className="cn-input-wrap">
                        <i className="ni ni-lock-circle-open cn-input-icon" aria-hidden="true" />
                        <input
                          id="login-password"
                          className="cn-input"
                          type="password"
                          autoComplete="current-password"
                          placeholder={t("login.password")}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="cn-error" role="alert">
                        {error}
                      </div>
                    )}

                    {/* REMEMBER ME BUTTON MAYBE ADD LATER!! */}

                    <button
                      className="cn-btn-primary cn-btn-block cn-btn-arrow"
                      type="submit"
                      disabled={isLogingIn}
                    >
                      {t("login.login")}
                    </button>
                  </form>
                </div>

                {/* Buttons, not anchors. The old markup used href="#" and
                    href="" alongside a navigate() onClick with no
                    preventDefault, so the browser followed the href as well —
                    the register link in particular reloaded the page. */}
                <div className="cn-links" data-reveal style={{ "--reveal-delay": "90ms" }}>
                  <button
                    className="cn-link"
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                  >
                    {t("login.forgotPassword")}
                  </button>
                  <button
                    className="cn-link"
                    type="button"
                    onClick={() => navigate("/register-page")}
                  >
                    {t("login.createNewAccount")}
                  </button>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default Login;
